import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpContextToken } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SpinnerService {
  public spinnerVisible$: Observable<boolean>;

  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private router = inject(Router);

  private readonly showDelay = 450; // ms
  private readonly hideDelay = 350; // ms

  private spinner: Element | null = null;
  private requests$ = new BehaviorSubject<Set<string>>(new Set<string>());
  private spinnerVisible: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor() {
    this.spinnerVisible$ = this.spinnerVisible.asObservable();

    // we only want to do this on the browser side, not the server side
    if (isPlatformBrowser(this.platformId)) {
      this.spinner = this.document.getElementById('page-spinner');

      this.watchForNavigation();
      this.watchForSpinner();
    }
  }

  // request based loading
  public startLoading(key: string) {
    const requests = new Set(this.requests$.getValue());
    requests.add(key);
    this.requests$.next(requests);
  }

  public stopLoading(key: string) {
    const requests = new Set(this.requests$.getValue());
    requests.delete(key);
    this.requests$.next(requests);
  }

  // router event based loading
  private watchForNavigation() {
    this.router.events
      .pipe(
        filter(
          (
            event
          ): event is NavigationStart | NavigationEnd | NavigationCancel =>
            event instanceof NavigationEnd ||
            event instanceof NavigationStart ||
            event instanceof NavigationCancel
        )
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.startLoading(event.url);
          return;
        }

        this.stopLoading(event.url);
      });
  }

  private watchForSpinner() {
    this.requests$
      .pipe(
        untilDestroyed(this),
        map((requests) => requests.size > 0),
        distinctUntilChanged(),
        switchMap((loading) => {
          // By incorporating delays here, we can prevent flashes of the spinner
          // by ensuring the spinner does not show if the request is fast enough
          // and by ensuring the spinner does not hide if the request completes
          // shortly after the spinner is shown
          if (loading) {
            return of(loading).pipe(delay(this.showDelay));
          }

          return of(loading).pipe(delay(this.hideDelay));
        })
      )
      .subscribe((loading) => {
        if (loading) {
          this.showSpinner();
        } else {
          this.hideSpinner();
        }
      });
  }

  private showSpinner() {
    if (this.spinner && !this.spinner.classList.contains('active')) {
      this.spinnerVisible.next(true);
      this.spinner.classList.add('active');
    }
  }

  private hideSpinner() {
    if (this.spinner && this.spinner.classList.contains('active')) {
      this.spinnerVisible.next(false);
      this.spinner.classList.remove('active');
    }
  }
}
